import { BitriseApp } from '@trace/types';
import { useContext, useEffect, useState } from 'react';

import { useQuery, UseQueryResult } from 'react-query';
import { AuthContextBitrise } from '../Authenticate/AuthenticateBitrise';
import { callBitriseApi } from '../../fetch/fetch';
import appendURLSearchParams from '../../utils/appendURLSearchParams';

/* eslint-disable camelcase */
export interface BitriseApiAppsResponse {
  data: BitriseApp[];
  paging: {
    next?: string;
    page_item_limit: number;
    total_item_count: number;
  };
}
/* eslint-enable camelcase */

type HookPayload = {
  apps: BitriseApp[];
  currentPage?: string;
  fetchNextPage: () => void;
  isFetched: boolean;
  nextPage?: string;
  prevAppsCount: number;
};

export type HookResult = Pick<UseQueryResult, 'refetch' | 'isFetching' | 'isError'> & HookPayload;

const useBitriseApps = (search: string): HookResult => {
  const { token } = useContext(AuthContextBitrise);
  const [payload, setPayload] = useState<
    Pick<HookPayload, 'apps' | 'currentPage' | 'isFetched' | 'nextPage' | 'prevAppsCount'>
  >({
    apps: [],
    isFetched: false,
    prevAppsCount: 0,
  });

  const { data, refetch, isFetching, isError } = useQuery<BitriseApiAppsResponse>(
    ['bitriseApps', { next: payload.currentPage, search, token }],
    async () =>
      callBitriseApi<BitriseApiAppsResponse>(
        'GET',
        `/apps?${appendURLSearchParams(
          '',
          ['limit', '5'],
          ['next', payload.currentPage],
          ['title', search],
        )}`,
        token,
      ),
  );

  const fetchNextPage = () => {
    setPayload((prevPayload) => ({
      ...prevPayload,
      currentPage: prevPayload.nextPage,
    }));
  };

  useEffect(() => {
    setPayload({
      apps: [],
      isFetched: false,
      prevAppsCount: 0,
    });
  }, [search]);

  /**
   * Use a side-effect to:
   *  - Update `payload` state, whenever the apps query resolves
   */
  useEffect(() => {
    if (data) {
      setPayload((prevPayload) => ({
        ...prevPayload,
        apps: [...prevPayload.apps, ...data.data],
        prevAppsCount: prevPayload.apps.length,
        nextPage: data.paging.next,
        isFetched: true,
      }));
    }
  }, [data]);

  return {
    fetchNextPage,
    isError,
    isFetching,
    refetch,
    ...payload,
  };
};

export default useBitriseApps;
